import styled from '@emotion/styled';
import {
  Button,
  Dialog,
  DialogActions as _DialogActions,
  DialogContent as _DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  Radio,
  Typography,
} from '@material-ui/core';
import { createElement, FC, Fragment } from 'react';
import { FormattedMessage } from 'react-intl';

import theme from '../../../../../theme';
import { useGetIsPC } from '../../../../../utilityHooks/useGetIsPC';
import InputtingCreativeFormatItem from '../../../../component/compound/BasicProjectSettingEditor/InputtingCreativeFormatItem';
import SelectCreativeTypes from '../../../../component/compound/BasicProjectSettingEditor/SelectCreativeTypes';
import SelectNetworksAndPlacements from '../../../../component/compound/BasicProjectSettingEditor/SelectNetworksAndPlacements';
import { SPACING_LARGE, SPACING_REGULAR, SPACING_UNIT } from '../../../../constant/spacing';
import mq from '../../../../styleUtility/mediaquery';
import { InputtingCreativeFormat } from '../../../../types/InputtingCreativeFormat';
import { InputtingCreativeFormatsFilter } from '../../../../types/InputtingCreativeFormatsFilter';
import { useEditingProjectState } from '../../hooks/useEditingProjectState';

type Props = {
  creativeFormats: ReadonlyArray<InputtingCreativeFormat>;
  inputtingCreativeFormatsFilter: InputtingCreativeFormatsFilter | null;
  selectedCreativeTypeIndex: Array<string | null> | null;
  selectedPlacementIndex: Array<string | null> | null;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  className?: string;
};

const AddOriginalFormatsDialog: FC<Props> = ({
  creativeFormats,
  inputtingCreativeFormatsFilter,
  selectedCreativeTypeIndex,
  selectedPlacementIndex,
  isOpen,
  onClose,
  onSubmit,
  className,
}) => {
  const {
    selectInputtingCreativeFormats,
    updateInputtingCreativeFormatSelectedPlaybackTime,
    setInputtingCreativeFormatsFilterByCreativeType,
    setInputtingCreativeFormatsFilterByNetworkAndPlacement,
    clearAllSelectedInputtingCreativeFormatsFilterByCreativeType,
    clearAllSelectedInputtingCreativeFormatsFilterByNetworkAndPlacement,
  } = useEditingProjectState();

  const isPC = useGetIsPC();
  const isStaticImageIncluded = inputtingCreativeFormatsFilter
    ? inputtingCreativeFormatsFilter.creativeTypes.some(
        (creativeType) => creativeType.creativeType.key === 'static_image',
      )
    : null;
  const isEmpty = creativeFormats.every((item) => !item.show);
  const isSelected = creativeFormats.some((creativeFormat) => creativeFormat.selected);

  return (
    <Dialog onClose={onClose} open={isOpen} maxWidth={'lg'} fullScreen={!isPC} className={className}>
      <DialogTitle>
        <FormattedMessage id="project_editing_page.basic_setting.creative_formats_dialog.add_original" />
      </DialogTitle>
      <DialogContent>
        <Selectors container direction="row" justifyContent="flex-start" alignItems="center" spacing={1}>
          {inputtingCreativeFormatsFilter ? (
            <>
              {/* 静止画制作が存在するProjectのみ表示 */}
              {isStaticImageIncluded ? (
                <Grid item xs={12} lg={4}>
                  <InputLabel shrink>
                    <Typography variant="caption">
                      <FormattedMessage id="project_editing_page.basic_setting.creative_formats_dialog.creative_types" />
                    </Typography>
                  </InputLabel>
                  <SelectCreativeTypes
                    inputtingCreativeFormatsFilter={inputtingCreativeFormatsFilter}
                    selectedCreativeTypeIndex={selectedCreativeTypeIndex}
                    onChangeSelectedCreativeType={(value) =>
                      value === 'all'
                        ? clearAllSelectedInputtingCreativeFormatsFilterByCreativeType()
                        : setInputtingCreativeFormatsFilterByCreativeType(Number(value))
                    }
                  />
                </Grid>
              ) : null}
              <Grid item xs={12} sm={12} md={4} lg={4}>
                <InputLabel shrink>
                  <Typography variant="caption">
                    <FormattedMessage id="project_editing_page.basic_setting.creative_formats_dialog.placements" />
                  </Typography>
                </InputLabel>
                <SelectNetworksAndPlacements
                  inputtingCreativeFormatsFilter={inputtingCreativeFormatsFilter}
                  selectedPlacementIndex={selectedPlacementIndex}
                  onChangeSelectedNetworksAndPlacements={(value) =>
                    value === 'all'
                      ? clearAllSelectedInputtingCreativeFormatsFilterByNetworkAndPlacement()
                      : setInputtingCreativeFormatsFilterByNetworkAndPlacement(Number(value))
                  }
                />
              </Grid>
            </>
          ) : null}
        </Selectors>
        <FormControl fullWidth>
          <Formats container direction="row" justifyContent="flex-start" alignItems="stretch" spacing={2}>
            {isEmpty && (
              <EmptyMessage>
                <Typography>
                  <FormattedMessage id="creative_format_editor.add_formats_dialog.empty_message" />
                </Typography>
              </EmptyMessage>
            )}
            {creativeFormats.map(
              (creativeFormat, index) =>
                creativeFormat.show && (
                  <FlexWrap key={`filteredList-${index}`}>
                    <CreativeFormatItem
                      onClick={() =>
                        selectInputtingCreativeFormats({
                          index,
                        })
                      }
                    >
                      <InputtingCreativeFormatItem
                        creativeFormatIndex={index}
                        creativeFormat={creativeFormat}
                        onChangePlaybackTime={updateInputtingCreativeFormatSelectedPlaybackTime}
                      />
                      <Radio
                        checked={creativeFormat.selected}
                        onChange={() =>
                          selectInputtingCreativeFormats({
                            index,
                          })
                        }
                        value={index}
                      />
                    </CreativeFormatItem>
                  </FlexWrap>
                ),
            )}
          </Formats>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button size="small" color="default" onClick={onClose}>
          <FormattedMessage id="project_editing_page.basic_setting.creative_formats_dialog.cancel" />
        </Button>
        <Button
          size="small"
          disabled={!isSelected}
          onClick={() => {
            onSubmit();
          }}
        >
          <FormattedMessage id="project_editing_page.basic_setting.creative_formats_dialog.submit" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DialogContent = styled(_DialogContent)`
  min-width: 960px;
  padding: 0 24px 8px;

  ${theme.breakpoints.down('md')} {
    min-width: auto;
  }
`;

const DialogActions = styled(_DialogActions)`
  ${mq('sp')} {
    flex-direction: column-reverse;

    > button {
      width: 100%;

      :not(:first-of-type) {
        margin-bottom: ${SPACING_UNIT}px;
        margin-left: 0;
      }
    }
  }
`;

const Selectors = styled(Grid)`
  position: sticky;
  top: 0;
  margin-bottom: ${SPACING_LARGE}px;
  background: #fff;
  z-index: 100;
`;

const Formats = styled(Grid)`
  width: 100%;
  margin-bottom: ${SPACING_REGULAR}px;

  ${mq('sp')} {
    margin: 0;
  }
`;

const EmptyMessage = styled.div`
  padding: ${SPACING_REGULAR}px;
  text-align: center;
`;

const FlexWrap = styled.div`
  display: flex;
  width: 25%;
  padding: 8px;

  ${mq('sp')} {
    width: 50%;
    padding: 8px 16px;
  }
`;

const CreativeFormatItem = styled.div`
  width: 100%;
  height: 100%;
  text-align: center;
  display: flex;
  flex-direction: column;
`;

export default AddOriginalFormatsDialog;
